import { CompRef } from '@wix/document-services-types';
import { ComponentRef } from '@wix/editor-platform-sdk-types';
import { memoize, minBy } from 'lodash';

import { DocumentServices } from '../../types';

type ComponentTreeItem = {
  componentRef: CompRef;
  role: string;
  children?: ComponentTreeItem[];
};

const buildComponentTree = memoize(
  (ds: DocumentServices, componentRef: CompRef): ComponentTreeItem => {
    const connection =
      ds.platform.controllers.connections.getPrimaryConnection(componentRef);

    return {
      componentRef,
      role: connection?.role,
      children: ds.deprecatedOldBadPerformanceApis.components
        .getChildren(componentRef)
        .map((c) => buildComponentTree(ds, c)),
    };
  },
  (_ds, compRef) => compRef.id,
);

export const findComponentBySelectorInternal = (
  item: ComponentTreeItem,
  selectors: string[],
  nestingLevel: number = 0,
): { item: ComponentTreeItem; nestingLevel: number }[] => {
  const topSelector = selectors[0];
  if (!topSelector) {
    return [];
  }

  if (topSelector === item.role) {
    selectors.shift();
  }

  if (selectors.length === 0) {
    return [{ item, nestingLevel }];
  }

  if (!item.children?.length) {
    return [];
  }

  const childResults = item.children?.map((child) => {
    return findComponentBySelectorInternal(
      child,
      selectors.slice(),
      nestingLevel++,
    );
  });
  return childResults.flatMap((x) => x);
};

export const findComponentBySelector = (
  ds: DocumentServices,
  rootCompRef: ComponentRef,
  selector: string,
) => {
  const roles = selector
    .split(' ')
    .map((x) => x.trim())
    .map((x) => x.slice(1));

  const componentTree = buildComponentTree(ds, rootCompRef);

  const foundElements = findComponentBySelectorInternal(componentTree, roles);

  return minBy(foundElements, (x) => x.nestingLevel)?.item;
};
